import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { components } from 'types/api'
import { getApiBaseUrl, prepareHeaders, getApiConfigKey } from 'utility/Api'
import apiConfig from './api-config'

export type SponsoredAccountAuths = components['schemas']['SponsoredAccountAuths']
export type SponsoredGuest = components['schemas']['SponsoredGuest']
export type SponsoredSpouse = components['schemas']['SponsoredSpouse']
export type Lookups = components['schemas']['Lookups']
export type SponsoredGuestCreated = components['schemas']['SponsoredGuestCreated']
export type SponsoredAccountStep = components['schemas']['RegistrationStatuses']
export type SponsoredGuestOverview = components['schemas']['SponsoredGuestOverview']
export type RenewSponsoredAccountRequest = components['schemas']['RenewSponsoredAccountRequest']
export type SponsoredGuestNewSponsor = components['schemas']['SponsoredGuestNewSponsor']
export type SponsorGuestReassignApproval = components['schemas']['SponsorGuestReassignApproval']
export type ReassignedSponsoredAccount = components['schemas']['ReassignedSponsoredAccount']
export type SponsoredGuestAdditionalDetails = components['schemas']['SponsoredGuestAdditionalDetails']

export type CostObject = components['schemas']['CostObject']

const baseUrl = getApiBaseUrl()
const configKey = getApiConfigKey()
const apiPath = apiConfig[configKey].paths.apiPathAccount ?? ''

// Define a service using a base URL and expected endpoints
export const SponsoredAccountApi = createApi({
  reducerPath: 'accountApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/${apiPath}`,
    prepareHeaders: async headers => await prepareHeaders(headers)
  }),
  tagTypes: ['GetSponsoredAccounts', 'RenewSponsoredAccouts'],
  endpoints: builder => ({
    getAuthorization: builder.query<SponsoredAccountAuths, void>({
      query: () => 'sponsored-account/authorization'
    }),
    createGuest: builder.mutation<void, SponsoredGuest>({
      query: data => ({
        url: 'sponsored-account/guest',
        method: 'POST', // When performing a mutation, you typically use a method of PATCH/PUT/POST/DELETE for REST endpoints
        body: data // fetchBaseQuery automatically adds `content-type: application/json` to the Headers and calls `JSON.stringify(patch)`
      }),
      invalidatesTags: (result, error, meta) => {
        if (error == null) {
          return ['GetSponsoredAccounts']
        }
        return []
      }
    }),
    createSpouse: builder.mutation<void, SponsoredSpouse>({
      query: data => ({
        url: 'sponsored-account/spouse',
        method: 'POST', // When performing a mutation, you typically use a method of PATCH/PUT/POST/DELETE for REST endpoints
        body: data // fetchBaseQuery automatically adds `content-type: application/json` to the Headers and calls `JSON.stringify(patch)`
      }),
      invalidatesTags: (result, error, meta) => {
        if (error == null) {
          return ['GetSponsoredAccounts']
        }
        return []
      }
    }),
    renewSponsoredAccounts: builder.mutation<void, RenewSponsoredAccountRequest>({
      query: data => ({
        url: 'sponsored-account/renewal',
        method: 'PUT', // When performing a mutation, you typically use a method of PATCH/PUT/POST/DELETE for REST endpoints
        body: data // fetchBaseQuery automatically adds `content-type: application/json` to the Headers and calls `JSON.stringify(patch)`
      }),
      invalidatesTags: (result, error, meta) => {
        if (error == null) {
          return ['GetSponsoredAccounts']
        }
        return []
      }
    }),
    reassignSponsoredAccount: builder.mutation<void, SponsoredGuestNewSponsor>({
      query: data => ({
        url: 'sponsored-account/reassign-sponsor',
        method: 'POST', // When performing a mutation, you typically use a method of PATCH/PUT/POST/DELETE for REST endpoints
        body: data // fetchBaseQuery automatically adds `content-type: application/json` to the Headers and calls `JSON.stringify(patch)`
      }),
      invalidatesTags: (result, error, meta) => {
        if (error == null) {
          return ['GetSponsoredAccounts']
        }
        return []
      }
    }),
    approveSponsoredAccountReassignment: builder.mutation<void, SponsorGuestReassignApproval & { mitId: string }>({
      query: ({ mitId, ...data }) => ({
        url: `sponsored-account/reassign-sponsor/${mitId}`,
        method: 'PUT', // When performing a mutation, you typically use a method of PATCH/PUT/POST/DELETE for REST endpoints
        body: data // fetchBaseQuery automatically adds `content-type: application/json` to the Headers and calls `JSON.stringify(patch)`
      }),
      invalidatesTags: (result, error, meta) => {
        if (error == null) {
          return ['GetSponsoredAccounts']
        }
        return []
      }
    }),
    getDepartments: builder.query<Lookups, void>({
      query: () => 'lookup/department'
    }),
    getSponsoredAccounts: builder.query<SponsoredGuestCreated, void>({
      query: () => 'sponsored-account',
      providesTags: ['GetSponsoredAccounts']
    }),
    getStepStatus: builder.query<SponsoredAccountStep, string>({
      query: mitId => `sponsored-account/${mitId}/status`
    }),
    getGuestAdditionalDetails: builder.query<SponsoredGuestAdditionalDetails, string>({
      query: mitId => `sponsored-account/${mitId}`
    }),
    getSponsoredAccountsToRenew: builder.query<SponsoredGuestOverview[], void>({
      query: () => 'sponsored-account/renewal',
      providesTags: ['GetSponsoredAccounts']
    }),
    getSponsoredAccountRequests: builder.query<ReassignedSponsoredAccount[], void>({
      query: () => 'sponsored-account/reassign-sponsor',
      providesTags: ['GetSponsoredAccounts']
    })
  })
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetAuthorizationQuery,
  useCreateGuestMutation,
  useRenewSponsoredAccountsMutation,
  useCreateSpouseMutation,
  useGetDepartmentsQuery,
  useGetSponsoredAccountsQuery,
  useGetStepStatusQuery,
  useGetGuestAdditionalDetailsQuery,
  useGetSponsoredAccountsToRenewQuery,
  useReassignSponsoredAccountMutation,
  useGetSponsoredAccountRequestsQuery,
  useApproveSponsoredAccountReassignmentMutation
} = SponsoredAccountApi
